import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  forwardRef,
  input,
  InputSignal,
  model,
  ModelSignal,
  OnInit,
  Signal,
  signal,
  viewChild,
  WritableSignal
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AddressyResponse } from '../../../../core/interfaces/addressy/api/addressy-api.interface';
import { BaseAddressyItem } from '../../../../core/interfaces/addressy/api/address/find-api.interface';
import {
  DeliveryAddressInfo,
  ShippingService
} from '../../../../core/services/shipping.service';
import { AddressyFindApiService } from '../../../../core/services/addressy/api/addressy-find-api.service';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR
} from '@angular/forms';
import { BehaviorSubject, debounceTime, skip, switchMap } from 'rxjs';
import { WooCommerceDeliveryAddress } from '../../../../core/interfaces/woo-commerce/api/woo-commerce-api.interface';
import { generateUUID } from '../../../utils/generate-uuid.util';
import { AddressyService } from '../../../../core/services/addressy/addressy.service';
import { convertAddressy2WooCommerce } from '../../../utils/addressy-2-woo-commerce.util';
import { AddressyItem } from '../../../../core/interfaces/addressy/api/address/retrieve-api.interface';

export enum InputAddressSize {
  Small = 'small',
  Medium = 'medium'
}

@UntilDestroy()
@Component({
  selector: 'app-input-address',
  templateUrl: './input-address.component.html',
  styleUrl: './input-address.component.scss',
  imports: [FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputAddressComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InputAddressComponent implements OnInit, ControlValueAccessor {
  private addressInput: Signal<ElementRef<HTMLInputElement>> =
    viewChild.required('addressInput');

  debounceTime: InputSignal<number> = input<number>(500);
  isShippingAvailable: ModelSignal<boolean> = model<boolean>(true);
  checkDelivery: InputSignal<boolean> = input(true);
  size: InputSignal<InputAddressSize> = input<InputAddressSize>(
    InputAddressSize.Small
  );

  // Address Control (For debounce)
  addressControl: WritableSignal<string> = signal<string>('');
  addressControl$: BehaviorSubject<string> = new BehaviorSubject<string>('');

  suggestions: WritableSignal<BaseAddressyItem[]> = signal([]);

  // State
  value: WritableSignal<WooCommerceDeliveryAddress | null> =
    signal<WooCommerceDeliveryAddress | null>(null);

  constructor(
    private addressyFindApiService: AddressyFindApiService,
    private shippingService: ShippingService,
    private addressyService: AddressyService
  ) {}

  ngOnInit() {
    this.subscribeToAddressControl();
  }

  onAddressChange(address: string): void {
    this.value.set(null);
    this.isShippingAvailable.set(true);
    this.addressControl$.next(address);
  }

  onAddressSelect(address: BaseAddressyItem) {
    this.addressControl.set(address.Text);

    if (this.checkDelivery()) {
      this.shippingService
        .getDeliveryAddressInfo(address)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: ({
            address,
            shippingMethods,
            isDeliverable
          }: DeliveryAddressInfo) => {
            this.isShippingAvailable.set(isDeliverable);
            this.changeValue({
              id: generateUUID(),
              shipping_methods: shippingMethods,
              ...address
            });

            if (isDeliverable) {
              this.suggestions.set([]);
            }
          }
        });
    } else {
      this.addressyService
        .getAddressDetails(address.Id, address.Text, address.Type)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: (response: AddressyResponse<AddressyItem[]>) => {
            if (response.Items && response.Items.length > 0) {
              const selectedAddress: AddressyItem = response.Items[0];
              const wooAddress = convertAddressy2WooCommerce(selectedAddress);
              this.isShippingAvailable.set(true);
              this.changeValue({
                id: generateUUID(),
                shipping_methods: [],
                ...wooAddress
              });
              this.suggestions.set([]);
            }
          }
        });
    }
  }

  onTryDifferentAddressClick() {
    this.value.set(null);
    this.suggestions.set([]);
    this.addressControl.set('');
    this.isShippingAvailable.set(true);
    this.addressInput().nativeElement.focus();
  }

  private subscribeToAddressControl() {
    this.addressControl$
      .pipe(
        skip(1),
        untilDestroyed(this),
        debounceTime(this.debounceTime()),
        switchMap((searchText: string) => {
          return this.addressyFindApiService.getAddresses({ searchText });
        })
      )
      .subscribe({
        next: (response: AddressyResponse<BaseAddressyItem[]>) => {
          this.suggestions.set(response.Items);
        }
      });
  }

  private changeValue(value: WooCommerceDeliveryAddress | null) {
    this.value.set(value);
    this.onChange?.(value);
  }

  // ControlValueAccessor functions
  private onChange?: (value: WooCommerceDeliveryAddress | null) => void;
  onTouch?: () => void;

  registerOnChange(
    fn: (value: WooCommerceDeliveryAddress | null) => void
  ): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  writeValue(outsideValue: WooCommerceDeliveryAddress | null): void {
    this.addressControl.set(outsideValue?.label || '');
    this.value.set(outsideValue);
  }
}
